import { useState } from "react";
import { CheckCircle2, ChevronDown, Shield, Wallet, Zap } from "lucide-react";
import { VenueOnboardingForm } from "@/features/venues/VenueOnboardingForm";
import { AppMockGallery, VENUE_AUDIENCE_SCREENS } from "@/components/brand/mocks/AppMockGallery";

export function VenuesPage() {
  return (
    <>
      {/* Hero + framed photo - grouped tight so the photo sits right
          under "Fill your courts with Whistll" instead of feeling like
          a separate section. Same framed-card shape as Landing. */}
      <section className="border-b border-border/60">
        <div className="mx-auto max-w-6xl px-6 pb-20 pt-20">
          <div className="max-w-3xl animate-fade-in">
            <p className="mb-4 text-sm font-medium uppercase tracking-widest text-muted-foreground">
              For venues
            </p>
            <h1 className="text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
              Fill your courts <span className="text-volt">with Whistll.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
              We connect basketball players with quality venues. List your
              courts, keep your existing booking flow, and get paid via Stripe
              Connect as soon as a game confirms.
            </p>
          </div>

          <div className="relative mx-auto mt-12 aspect-[4/3] w-full max-w-lg overflow-hidden rounded-2xl border border-border shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1598010314551-917b50405647?w=1600&q=80&auto=format&fit=crop"
              alt="Urban street scene - graffiti wall outside a court"
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent dark:from-background/70"
            />
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="border-b border-border/60 bg-secondary/30">
        <div className="mx-auto grid max-w-6xl gap-8 px-6 py-20 sm:grid-cols-2 lg:grid-cols-4">
          <Prop
            icon={<Zap className="h-5 w-5" />}
            title="No double bookings"
            body="Sync availability with the system you already use - Bookteq, Skedda, CourtReserve and others. No system? Use our free venue dashboard."
          />
          <Prop
            icon={<Wallet className="h-5 w-5" />}
            title="Paid on confirmation"
            body="You receive your full court fee via Stripe Connect the moment a game hits its player threshold."
          />
          <Prop
            icon={<Shield className="h-5 w-5" />}
            title="Verified players"
            body="Every player has a profile, a flag-and-ban system, and pre-authed payment before they can join a run."
          />
          <Prop
            icon={<CheckCircle2 className="h-5 w-5" />}
            title="Zero admin"
            body="No manual availability management, no chasing players. Confirmed bookings are pushed back to your system automatically."
          />
        </div>
      </section>

      {/* How your venue appears in the app */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[640px] w-[640px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-20 blur-3xl"
          style={{
            backgroundImage:
              "radial-gradient(closest-side, hsl(var(--volt)), transparent 70%)",
          }}
        />
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <p className="mb-4 text-sm font-medium uppercase tracking-widest text-muted-foreground">
              What players see
            </p>
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              Your venue, front and centre in the Whistll app.
            </h2>
            <p className="mt-4 text-muted-foreground">
              Players find your courts across every screen: the home
              dashboard, the open runs feed, the court browser, and the full
              venue page. Swipe through to see exactly how your listing
              surfaces.
            </p>
            <ul className="mt-8 mx-auto grid max-w-xl gap-3 text-left text-sm sm:grid-cols-2">
              <Feature>Hero image + gallery on every card</Feature>
              <Feature>Live pricing in the player's currency</Feature>
              <Feature>Ratings and reviews build your reputation</Feature>
              <Feature>Stripe Connect payouts on confirmed games</Feature>
            </ul>
          </div>

          <div className="mt-12">
            <AppMockGallery screens={VENUE_AUDIENCE_SCREENS} />
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="border-b border-border/60">
        <div className="mx-auto max-w-3xl px-6 py-20">
          <div className="mb-10">
            <h2 className="text-3xl font-semibold tracking-tight">
              Apply to become a partner
            </h2>
            <p className="mt-3 text-muted-foreground">
              Tell us about your venue. Applications are reviewed within
              48 hours. We'll follow up with Stripe Connect onboarding and
              booking system integration steps once approved.
            </p>
          </div>
          <VenueOnboardingForm />
        </div>
      </section>

      {/* FAQ */}
      <section>
        <div className="mx-auto max-w-3xl px-6 py-20">
          <div className="mb-10">
            <p className="mb-3 text-sm font-medium uppercase tracking-widest text-muted-foreground">
              FAQ
            </p>
            <h2 className="text-3xl font-semibold tracking-tight">
              Things venue partners usually ask
            </h2>
          </div>

          <div className="flex flex-col divide-y divide-border rounded-2xl border border-border bg-card">
            <FaqItem
              q="What does it cost to list with Whistll?"
              a="Zero listing fees, zero monthly minimums. Whistll charges a 20% fee on top of the player price, and it's never deducted from what your venue gets paid. If no games confirm, you pay nothing."
            />
            <FaqItem
              q="How do I get paid?"
              a="Via Stripe Connect, direct to your bank. You complete Stripe onboarding once during setup. When a game confirms (hits its player threshold), your full court fee is paid out automatically. No invoices, no chasing."
            />
            <FaqItem
              q="I already use Bookteq / Skedda / CourtReserve. Do I have to switch?"
              a="No. We work with most major booking systems. The cleanest setup is a webhook from your system to ours - it pushes availability changes to Whistll the moment they happen, no double entry, no polling delays. We're happy to walk your booking-system admin through the config. If your system doesn't expose webhooks, or you don't use one at all, you can manage availability directly in our free venue dashboard. Either way works."
            />
            <FaqItem
              q="What if a game cancels after I've already been paid out?"
              a="Whistll only pays out once a game is confirmed (threshold hit, payments captured). If all players cancel post-confirmation, the clawback policy we agree at onboarding kicks in. If the game doesn't confirm, nothing is paid and players are fully refunded automatically."
            />
            <FaqItem
              q="How do you handle double bookings?"
              a="If your booking system pushes availability to us in real time (webhook), we never offer a slot your system is already holding. For venues using the Whistll dashboard directly, availability is the single source of truth. Within Whistll, when two games race for the same slot, the database guarantees only one can fill - the other auto-cancels with full refunds."
            />
            <FaqItem
              q="Who is responsible for players who cause problems at the venue?"
              a="Every player has a verified profile, a flag-and-ban track record, and pre-authed payment before they can join a run. Flags are visible in our admin panel; repeat offenders are automatically banned via our 2-flags-in-90-days rule. If your staff need to flag a player, you can do it from the venue dashboard."
            />
            <FaqItem
              q="What countries are you live in?"
              a="We're onboarding venues in the US, UK, Canada, Australia, and New Zealand simultaneously. Pricing and payouts happen in local currency per venue; a player booking a court abroad pays in the venue's currency with the FX shown up front."
            />
            <FaqItem
              q="How long does onboarding take?"
              a="Applications are reviewed within 48 hours. Stripe Connect typically completes inside a day once you submit their form. If you use one of the booking systems we've integrated, your courts can be live in the app within a week of applying."
            />
          </div>
        </div>
      </section>
    </>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen((x) => !x)}
      className="flex w-full flex-col gap-2 px-6 py-5 text-left transition-colors first:rounded-t-2xl last:rounded-b-2xl hover:bg-secondary/40"
      aria-expanded={open}
    >
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-base font-semibold sm:text-lg">{q}</h3>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
            open ? "rotate-180 text-volt" : ""
          }`}
          aria-hidden
        />
      </div>
      {open && (
        <p className="pr-8 text-sm leading-relaxed text-muted-foreground">
          {a}
        </p>
      )}
    </button>
  );
}

function Prop({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-volt text-primary-foreground">
        {icon}
      </div>
      <h3 className="text-base font-semibold">{title}</h3>
      <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

function Feature({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-volt" />
      <span className="text-muted-foreground">{children}</span>
    </li>
  );
}
